import React, { useCallback, useEffect, useState } from 'react';
import { Save, RotateCcw, Settings2, AlertCircle, CheckCircle } from 'lucide-react';
import ReadOnlyFieldset, { ReadOnlyNote } from './ui/ReadOnlyFieldset';
import Button from './ui/Button';
import Input from './ui/Input';
import pimTierApiService from '../services/pimTierApiService';
import { trackException } from '../services/appInsightsService';

/**
 * PimTiersSection — Plans tab subsection for editing per-tier PIM (Translate Around Me)
 * capability overrides (max race depth, allowed models, default model, hedge toggle, retail price).
 *
 * Sprint P8c. Backed by the GET/PUT/DELETE /api/platform/pim-tiers endpoints.
 * The 3 tiers are platform-wide overrides keyed by lowercase tier name; they layer on top
 * of the code defaults defined in TierCaps.cs.
 */

/**
 * The PIM tier catalog, in display order. Mirrors `SupportedTiers` in
 * PimTierAdminFunctions.cs, which is itself driven by TierCaps.KnownTiers —
 * GET /platform/pim-tiers returns exactly these names and nothing else.
 * Exported so tests assert against the catalog instead of re-hardcoding tier
 * names (they were renamed once already: Pro -> Plus, Professional -> Pro in #879).
 */
export const SUPPORTED_TIERS = ['Starter', 'Plus', 'Pro'];

// The pill's TEXT uses --text-primary (theme-aware, always contrast-safe against --bg-card) --
// --primary stays on the background only, as a decorative tint distinguishing the three tiers.
// --primary's own raw hex maxes out at ~4.47:1 against white and gets worse on any darker
// background, so it can never serve as text at AA regardless of what it is mixed with.
const TIER_PILL_STYLE = {
    Starter: { background: 'color-mix(in srgb, var(--primary) 12%, var(--bg-card))', color: 'var(--text-primary)' },
    Plus: { background: 'color-mix(in srgb, var(--primary) 20%, var(--bg-card))', color: 'var(--text-primary)' },
    Pro: { background: 'color-mix(in srgb, var(--primary) 30%, var(--bg-card))', color: 'var(--text-primary)' },
};

const sectionStyle = {
    padding: '24px',
    borderTop: '1px solid var(--border-color, #e5e7eb)',
    marginTop: '24px',
};

const cardStyle = {
    background: 'var(--bg-card, #fff)',
    border: '1px solid var(--border-color, #e5e7eb)',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '16px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
};

const headerRowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
};

const fieldRowStyle = {
    display: 'grid',
    gridTemplateColumns: '180px 1fr',
    gap: '12px',
    alignItems: 'center',
    marginBottom: '12px',
};

/**
 * `--error`/`--success` are the same static hex in both themes, so neither can serve as TEXT on
 * a background tinted from --bg-card: a 12%-into-white (light mode) tint and a 12%-into-#1e293b
 * (dark mode) tint sit at opposite ends of the luminance range, and a single static colour can
 * satisfy AA contrast against only one of them. --text-primary already flips per theme to
 * contrast against --bg-card, and a 12% tint barely moves off it, so it stays readable in both.
 * The icon alone (see noticeIconStyle below) carries the error/success colour instead.
 */
const noticeTextStyle = { color: 'var(--text-primary, #111827)' };
/** Semantic colour for a notice's icon only -- its text uses noticeTextStyle's --text-primary. */
const noticeIconStyle = (isError) => ({
    color: isError ? 'var(--error, #dc2626)' : 'var(--success, #059669)',
    flexShrink: 0,
});

const buttonRowStyle = {
    display: 'flex',
    gap: '12px',
    marginTop: '16px',
};

/**
 * Empty form state for a tier card.
 */
const buildEditForm = (tier) => ({
    maxActiveRace: tier.maxActiveRace ?? '',
    allowedModels: Array.isArray(tier.allowedModels) ? tier.allowedModels.join('\n') : '',
    defaultModel: tier.defaultModel ?? '',
    hedgeEnabled: !!tier.hedgeEnabled,
    retailPricePerHourCents: tier.retailPricePerHourCents ?? '',
});

const PimTierCard = ({ tier, onSave, onRevert, savingTier, readOnly }) => {
    const [form, setForm] = useState(() => buildEditForm(tier));
    const [localStatus, setLocalStatus] = useState(null);

    // Re-sync the local form when the parent re-fetches the tier list.
    useEffect(() => {
        setForm(buildEditForm(tier));
    }, [tier]);

    const handleSave = async () => {
        if (readOnly) return;
        setLocalStatus(null);
        const allowedModels = form.allowedModels
            .split('\n')
            .map((m) => m.trim())
            .filter((m) => m.length > 0);

        const body = {
            MaxActiveRace: form.maxActiveRace === '' ? null : Number(form.maxActiveRace),
            AllowedModels: allowedModels.length > 0 ? allowedModels : null,
            DefaultModel: form.defaultModel || null,
            HedgeEnabled: form.hedgeEnabled,
            RetailPricePerHourCents: form.retailPricePerHourCents === '' ? null : Number(form.retailPricePerHourCents),
        };

        try {
            await onSave(tier.tierName, body);
            setLocalStatus({ type: 'success', message: 'Saved.' });
        } catch (err) {
            trackException(err, { operation: 'PimTiers.save', tier: tier.tierName });
            setLocalStatus({ type: 'error', message: err.message || 'Save failed.' });
        }
    };

    const handleRevert = async () => {
        if (readOnly) return;
        setLocalStatus(null);
        try {
            await onRevert(tier.tierName);
            setLocalStatus({ type: 'success', message: 'Reverted to default.' });
        } catch (err) {
            trackException(err, { operation: 'PimTiers.revert', tier: tier.tierName });
            setLocalStatus({ type: 'error', message: err.message || 'Revert failed.' });
        }
    };

    const isSaving = savingTier === tier.tierName;
    const pillStyle = TIER_PILL_STYLE[tier.tierName] || {};

    return (
        <div
            style={cardStyle}
            data-testid={`pim-tier-card-${tier.tierName.toLowerCase()}`}
        >
            <div style={headerRowStyle}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <h4 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>{tier.tierName}</h4>
                    <span
                        style={{
                            padding: '4px 10px',
                            borderRadius: '999px',
                            fontSize: '12px',
                            fontWeight: 600,
                            ...pillStyle,
                        }}
                    >
                        {tier.hasOverride ? 'Override active' : 'Default'}
                    </span>
                </div>
                {tier.hasOverride && tier.lastModifiedBy && (
                    <span style={{ fontSize: '12px', color: 'var(--text-secondary, #6b7280)' }}>
                        Modified by {tier.lastModifiedBy}
                        {tier.lastModifiedAtUtc ? ` at ${new Date(tier.lastModifiedAtUtc).toLocaleString()}` : ''}
                    </span>
                )}
            </div>

            <div style={fieldRowStyle}>
                <label htmlFor={`max-active-race-${tier.tierName}`} style={{ fontSize: '14px' }}>
                    Max Active Race
                </label>
                <Input
                    id={`max-active-race-${tier.tierName}`}
                    type="number"
                    min={1}
                    max={10}
                    data-field="maxActiveRace"
                    value={form.maxActiveRace}
                    onChange={(e) => setForm((f) => ({ ...f, maxActiveRace: e.target.value }))}
                />
            </div>

            <div style={fieldRowStyle}>
                <label htmlFor={`allowed-models-${tier.tierName}`} style={{ fontSize: '14px' }}>
                    Allowed Models (one per line)
                </label>
                <textarea
                    id={`allowed-models-${tier.tierName}`}
                    data-field="allowedModels"
                    rows={3}
                    value={form.allowedModels}
                    onChange={(e) => setForm((f) => ({ ...f, allowedModels: e.target.value }))}
                    style={{
                        padding: '8px 12px',
                        borderRadius: '6px',
                        border: '1px solid var(--border-color, #d1d5db)',
                        fontFamily: 'var(--font-mono, monospace)',
                        fontSize: '13px',
                    }}
                />
            </div>

            <div style={fieldRowStyle}>
                <label htmlFor={`default-model-${tier.tierName}`} style={{ fontSize: '14px' }}>
                    Default Model
                </label>
                <Input
                    id={`default-model-${tier.tierName}`}
                    type="text"
                    data-field="defaultModel"
                    value={form.defaultModel}
                    onChange={(e) => setForm((f) => ({ ...f, defaultModel: e.target.value }))}
                />
            </div>

            <div style={fieldRowStyle}>
                <label htmlFor={`hedge-enabled-${tier.tierName}`} style={{ fontSize: '14px' }}>
                    Hedge Enabled
                </label>
                <input
                    id={`hedge-enabled-${tier.tierName}`}
                    type="checkbox"
                    data-field="hedgeEnabled"
                    checked={form.hedgeEnabled}
                    onChange={(e) => setForm((f) => ({ ...f, hedgeEnabled: e.target.checked }))}
                />
            </div>

            <div style={fieldRowStyle}>
                <label htmlFor={`retail-price-${tier.tierName}`} style={{ fontSize: '14px' }}>
                    Retail Price Per Hour (cents)
                </label>
                <Input
                    id={`retail-price-${tier.tierName}`}
                    type="number"
                    min={0}
                    max={100000}
                    data-field="retailPricePerHourCents"
                    value={form.retailPricePerHourCents}
                    onChange={(e) => setForm((f) => ({ ...f, retailPricePerHourCents: e.target.value }))}
                />
            </div>

            <div style={buttonRowStyle}>
                <Button
                    onClick={handleSave}
                    disabled={readOnly}
                    isLoading={isSaving}
                    icon={<Save size={16} />}
                    variant="primary"
                    size="sm"
                >
                    Save
                </Button>
                <Button
                    onClick={handleRevert}
                    disabled={readOnly || !tier.hasOverride || isSaving}
                    icon={<RotateCcw size={16} />}
                    variant="secondary"
                    size="sm"
                >
                    Revert to default
                </Button>
            </div>

            {localStatus && (
                <div
                    style={{
                        marginTop: '12px',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        fontSize: '13px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        background: localStatus.type === 'success' ? 'color-mix(in srgb, var(--success, #059669) 12%, var(--bg-card))' : 'color-mix(in srgb, var(--error, #dc2626) 12%, var(--bg-card))',
                        ...noticeTextStyle,
                    }}
                >
                    {localStatus.type === 'success'
                        ? <CheckCircle size={16} style={noticeIconStyle(false)} />
                        : <AlertCircle size={16} style={noticeIconStyle(true)} />}
                    {localStatus.message}
                </div>
            )}
        </div>
    );
};

const PimTiersSection = ({ readOnly = false }) => {
    const [tiers, setTiers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [savingTier, setSavingTier] = useState(null);
    const [loadError, setLoadError] = useState(null);

    const loadTiers = useCallback(async () => {
        setIsLoading(true);
        setLoadError(null);
        try {
            const data = await pimTierApiService.listTiers();
            const list = Array.isArray(data?.tiers) ? data.tiers : [];

            // If the backend response is missing some tiers, surface placeholders so the UI is consistent.
            const byName = new Map(list.map((t) => [t.tierName, t]));
            const ordered = SUPPORTED_TIERS.map((name) => byName.get(name) || {
                tierName: name,
                maxActiveRace: '',
                allowedModels: [],
                defaultModel: '',
                hedgeEnabled: false,
                retailPricePerHourCents: '',
                hasOverride: false,
            });
            setTiers(ordered);
        } catch (err) {
            trackException(err, { operation: 'PimTiers.load' });
            setLoadError(err.message || 'Failed to load PIM tiers.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadTiers();
    }, [loadTiers]);

    const handleSave = async (tierName, body) => {
        setSavingTier(tierName);
        try {
            await pimTierApiService.saveOverride(tierName, body);
            await loadTiers();
        } finally {
            setSavingTier(null);
        }
    };

    const handleRevert = async (tierName) => {
        setSavingTier(tierName);
        try {
            await pimTierApiService.clearOverride(tierName);
            await loadTiers();
        } finally {
            setSavingTier(null);
        }
    };

    return (
        <section style={sectionStyle} data-testid="pim-tiers-section">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <Settings2 size={20} />
                <h3 style={{ margin: 0 }}>Translate Around Me (PIM) Tiers</h3>
            </div>
            <p style={{ color: 'var(--text-secondary, #6b7280)', marginTop: 0, marginBottom: '16px' }}>
                Override the default capability caps for each PIM tier. Changes take effect on the next request
                (60s resolver cache). Leaving a field blank uses the code default.
            </p>

            {loadError && (
                <div
                    style={{
                        padding: '12px',
                        borderRadius: '8px',
                        background: 'color-mix(in srgb, var(--error, #dc2626) 12%, var(--bg-card))',
                        marginBottom: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        ...noticeTextStyle,
                    }}
                >
                    <AlertCircle size={18} style={noticeIconStyle(true)} />
                    {loadError}
                </div>
            )}

            <ReadOnlyNote readOnly={readOnly} />

            <ReadOnlyFieldset readOnly={readOnly}>
            {isLoading && tiers.length === 0
                ? <p>Loading tiers…</p>
                : tiers.map((tier) => (
                    <PimTierCard
                        key={tier.tierName}
                        tier={tier}
                        onSave={handleSave}
                        onRevert={handleRevert}
                        savingTier={savingTier}
                        readOnly={readOnly}
                    />
                ))}
            </ReadOnlyFieldset>
        </section>
    );
};

export default PimTiersSection;
